<template>
	<div data-tour="vat">
		<SectionCard title="IVA del periodo" icon="mdi-percent-outline" subtitle="indicativa, non sostituisce la liquidazione del commercialista">
			<div class="fv-totals">
				<div>
					<div class="fv-muted">IVA a debito (vendite)</div>
					<div class="fv-big">{{ formatEurCents(totals.salesVat) }}</div>
				</div>
				<div class="fv-op">−</div>
				<div>
					<div class="fv-muted">IVA detraibile (acquisti e costi)</div>
					<div class="fv-big">{{ formatEurCents(totals.deductible) }}</div>
				</div>
				<div class="fv-op">=</div>
				<div>
					<div class="fv-muted">{{ totals.balance >= 0 ? 'Saldo a debito' : 'Saldo a credito' }}</div>
					<div class="fv-big" :class="totals.balance >= 0 ? 'red--text text--darken-1' : 'green--text text--darken-1'">{{ formatEurCents(Math.abs(totals.balance || 0)) }}</div>
				</div>
				<div v-if="totals.nonDeductible" class="ml-md-6">
					<div class="fv-muted">IVA indetraibile (finita nei costi)</div>
					<div class="fv-big">{{ formatEurCents(totals.nonDeductible) }}</div>
				</div>
				<div v-if="totals.ossVat" class="ml-md-6">
					<div class="fv-muted">IVA OSS (si versa a parte)</div>
					<div class="fv-big">{{ formatEurCents(totals.ossVat) }}</div>
				</div>
			</div>
			<v-alert v-if="warnings.ordersWithoutVat || warnings.invalidVatLines" dense text type="warning" class="mt-3 mb-0">
				{{ formatNumber(warnings.ordersWithoutVat) }} ordini hanno righe senza aliquota{{ warnings.invalidVatLines ? ` (${warnings.invalidVatLines} con un'aliquota impossibile)` : '' }}: sono nel box
				«0% / non indicata» e non vengono attribuiti al 22%. Di solito sono ordini marketplace con prodotti senza IVA in scheda.
			</v-alert>
		</SectionCard>

		<v-row dense class="mt-2">
			<v-col v-for="box in boxes" :key="box.key" cols="12" sm="6" lg="4" xl="3">
				<v-card outlined class="fv-box">
					<div class="fv-box-head">
						<span class="fv-box-title">{{ vatLabel(box.key) }}</span>
						<v-spacer />
						<v-btn icon small :title="open[box.key] ? 'Nascondi dettaglio' : 'Dettaglio per fonte'" @click="toggle(box.key)">
							<v-icon small>{{ open[box.key] ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
						</v-btn>
					</div>
					<div class="fv-grid">
						<div class="fv-muted">Vendite imponibile</div>
						<div class="text-right">{{ formatEurCents(box.sales.net) }}</div>
						<div class="fv-muted">IVA a debito</div>
						<div class="text-right">{{ formatEurCents(box.sales.vat) }}</div>
						<div class="fv-muted">Acquisti e costi imponibile</div>
						<div class="text-right">{{ formatEurCents(box.purchases.net) }}</div>
						<div class="fv-muted">IVA detraibile</div>
						<div class="text-right">{{ formatEurCents(box.purchases.deductible) }}</div>
						<template v-if="box.purchases.nonDeductible">
							<div class="fv-muted">IVA indetraibile</div>
							<div class="text-right">{{ formatEurCents(box.purchases.nonDeductible) }}</div>
						</template>
						<div class="fv-strong">Saldo</div>
						<div class="text-right fv-strong" :class="{ 'red--text text--darken-1': box.balance > 0, 'green--text text--darken-1': box.balance < 0 }">{{ formatEurCents(box.balance) }}</div>
					</div>
					<div v-if="box.key === '0:reverse_charge'" class="fv-muted mt-2">Commissioni e servizi da fornitori esteri: IVA a debito e detraibile si compensano.</div>
					<div v-else-if="box.key === '0:oss'" class="fv-muted mt-2">
						Vendite B2C ad altri Paesi UE dopo l'adesione all'OSS: IVA del Paese del cliente, dichiarata con l'OSS e fuori dalla liquidazione italiana.
						<router-link to="/finance/foreign">Apri il report IVA estera</router-link>
					</div>
					<div v-else-if="box.key === '0:non_imponibile'" class="fv-muted mt-2">Comprende export extra-UE e cessioni a clienti UE con partita IVA (report IVA estera).</div>
					<v-simple-table v-if="open[box.key]" dense class="mt-2">
						<thead>
							<tr>
								<th>Fonte</th>
								<th class="text-right">Vendite</th>
								<th class="text-right">Acquisti</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(v, source) in box.bySource" :key="source">
								<td>{{ sourceLabel(source) }}</td>
								<td class="text-right">{{ v.salesNet ? formatEurCents(v.salesNet) : '—' }}</td>
								<td class="text-right">{{ v.purchasesNet ? formatEurCents(v.purchasesNet) : '—' }}</td>
							</tr>
						</tbody>
					</v-simple-table>
				</v-card>
			</v-col>
			<v-col v-if="!boxes.length" cols="12">
				<div class="text-center grey--text py-6">Nessun movimento IVA nel periodo.</div>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { formatEurCents, formatNumber, vatLabel, sourceLabel } from './format';

export default {
	name: 'FinanceVat',
	components: { SectionCard },
	props: {
		report: { type: Object, required: true },
	},
	data() {
		return { open: {} };
	},
	computed: {
		boxes() {
			return (this.report.vat && this.report.vat.boxes) || [];
		},
		totals() {
			return (this.report.vat && this.report.vat.totals) || {};
		},
		warnings() {
			return this.report.warnings || {};
		},
	},
	methods: {
		formatEurCents,
		formatNumber,
		vatLabel,
		sourceLabel,
		toggle(key) {
			this.$set(this.open, key, !this.open[key]);
		},
	},
};
</script>

<style scoped>
.fv-totals {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 12px 24px;
}
.fv-op {
	font-size: 22px;
	color: #8a96a3;
	padding-bottom: 2px;
}
.fv-big {
	font-size: 20px;
	font-weight: 600;
}
.fv-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.fv-strong {
	font-weight: 600;
}
.fv-box {
	border-radius: 10px !important;
	padding: 12px 14px;
	height: 100%;
}
.fv-box-head {
	display: flex;
	align-items: center;
	margin-bottom: 6px;
}
.fv-box-title {
	font-weight: 600;
	font-size: 15px;
}
.fv-grid {
	display: grid;
	grid-template-columns: 1fr auto;
	row-gap: 4px;
	column-gap: 12px;
	font-size: 14px;
}
</style>
